import { useEffect, useMemo, useRef, useState } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import cytoscape, { type Core, type EventObjectEdge, type EventObjectNode } from "cytoscape";
import coseBilkent from "cytoscape-cose-bilkent";
import { Crosshair, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { createAffinityChartElements } from "@/features/affinity-chart/affinity-chart-elements";
import type {
  AffinityChartEdge,
  AffinityChartLayoutPositions,
  AffinityChartNode,
  AffinityChartSelection,
} from "@/features/affinity-chart/affinity-chart.types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

cytoscape.use(coseBilkent);

type AffinityChartCanvasProps = {
  edges: AffinityChartEdge[];
  focusRequestToken: number;
  layoutRevision: number;
  nodes: AffinityChartNode[];
  onBackgroundSelect: () => void;
  onEdgeSelect: (edgeId: string) => void;
  onNodeSelect: (nodeId: string) => void;
  onPositionsChange: (positions: AffinityChartLayoutPositions) => void;
  onPositionsCommit: (positions: AffinityChartLayoutPositions) => void;
  positions: AffinityChartLayoutPositions;
  selection: AffinityChartSelection;
};

const ENTITY_COLORS: Record<string, string> = {
  character: "#d07a35",
  creature: "#3b8d66",
  "item-object": "#2d6fa0",
  location: "#2f7a61",
  machine: "#6f5a91",
};

const LORE_COLORS: Record<string, string> = {
  culture: "#7e6947",
  faction: "#8655a9",
  location: "#2f7a61",
  magic_system: "#7054a6",
  political_system: "#8a6a31",
  race_species: "#6f6235",
  religion: "#9a4f67",
  style_guide: "#3d6886",
  technology: "#356692",
};

function createStylesheet() {
  const styles: Array<{
    selector: string;
    style: Record<string, number | string>;
  }> = [
    {
      selector: "node",
      style: {
        "background-color": "#c6c6c6",
        "background-clip": "node",
        "background-fit": "cover",
        "background-height": "100%",
        "background-image": "data(imageSrc)",
        "background-position-x": "50%",
        "background-position-y": "50%",
        "background-width": "100%",
        "border-color": "#5e5e5e",
        "border-width": 2,
        color: "#1f2933",
        "font-size": 10,
        "font-weight": 700,
        label: "data(label)",
        "min-zoomed-font-size": 8,
        "text-events": "no",
        "text-halign": "center",
        "text-margin-y": 8,
        "text-max-width": 120,
        "text-valign": "bottom",
        "text-wrap": "wrap",
      },
    },
    {
      selector: 'node[family = "entity-card"]',
      style: {
        height: 58,
        shape: "ellipse",
        width: 58,
      },
    },
    {
      selector: 'node[family = "lore-entry"]',
      style: {
        height: 70,
        shape: "diamond",
        width: 70,
      },
    },
    {
      selector: "edge",
      style: {
        "curve-style": "bezier",
        "font-size": 10,
        label: "data(label)",
        "line-color": "#9d917f",
        "target-arrow-color": "#9d917f",
        "target-arrow-shape": "triangle",
        "text-background-color": "#fffaf4",
        "text-background-opacity": 1,
        "text-background-padding": 2,
        "text-rotation": "autorotate",
        width: 2,
      },
    },
    {
      selector: ":selected",
      style: {
        "border-color": "#a64e24",
        "border-width": 4,
        "line-color": "#a64e24",
        "target-arrow-color": "#a64e24",
      },
    },
  ];

  Object.entries(ENTITY_COLORS).forEach(([type, color]) => {
    styles.push({
      selector: `node[type = "${type}"]`,
      style: {
        "background-color": color,
      },
    });
  });

  Object.entries(LORE_COLORS).forEach(([type, color]) => {
    styles.push({
      selector: `node[type = "${type}"]`,
      style: {
        "background-color": color,
      },
    });
  });

  return styles;
}

const STYLESHEET = createStylesheet();

function collectNodePositions(cy: Core) {
  return Object.fromEntries(
    cy.nodes().map((node) => [
      node.id(),
      {
        x: node.position("x"),
        y: node.position("y"),
      },
    ]),
  );
}

export function AffinityChartCanvas({
  edges,
  focusRequestToken,
  layoutRevision,
  nodes,
  onBackgroundSelect,
  onEdgeSelect,
  onNodeSelect,
  onPositionsChange,
  onPositionsCommit,
  positions,
  selection,
}: AffinityChartCanvasProps) {
  const { t } = useTranslation();
  const browserRef = useRef<HTMLDivElement | null>(null);
  const cyRef = useRef<Core | null>(null);
  const previousLayoutRevisionRef = useRef(layoutRevision);
  const previousNodeIdsKeyRef = useRef<string | null>(null);
  const previousPositionsKeyRef = useRef<string | null>(null);
  const handlersRef = useRef({
    onBackgroundSelect,
    onEdgeSelect,
    onNodeSelect,
    onPositionsChange,
    onPositionsCommit,
  });
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [browserOpen, setBrowserOpen] = useState(false);
  const elements = useMemo(
    () => createAffinityChartElements(nodes, edges, positions),
    [edges, nodes, positions],
  );
  const nodeIdsKey = useMemo(() => nodes.map((node) => node.id).join("|"), [nodes]);
  const positionsKey = useMemo(() => JSON.stringify(positions), [positions]);

  handlersRef.current = {
    onBackgroundSelect,
    onEdgeSelect,
    onNodeSelect,
    onPositionsChange,
    onPositionsCommit,
  };

  function attachCy(cy: Core) {
    if (cyRef.current === cy) {
      return;
    }

    cyRef.current = cy;
    if (typeof window !== "undefined") {
      // Expose the live graph to Playwright so layout persistence can be tested
      // against the real Cytoscape instance.
      (window as typeof window & { __affinityChartCy?: Core | null }).__affinityChartCy = cy;
    }

    cy.on("tap", "node", (event: EventObjectNode) => {
      handlersRef.current.onNodeSelect(event.target.id());
    });

    cy.on("tap", "edge", (event: EventObjectEdge) => {
      handlersRef.current.onEdgeSelect(event.target.id());
    });

    cy.on("tap", (event) => {
      if (event.target === cy) {
        handlersRef.current.onBackgroundSelect();
      }
    });

    cy.on("dragfree", "node", () => {
      const nextPositions = collectNodePositions(cy);
      handlersRef.current.onPositionsChange(nextPositions);

      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
      }

      persistTimerRef.current = setTimeout(() => {
        handlersRef.current.onPositionsCommit(collectNodePositions(cy));
      }, 1000);
    });

    cy.on("layoutstop", () => {
      handlersRef.current.onPositionsChange(collectNodePositions(cy));
    });
  }

  useEffect(() => {
    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
      }

      if (typeof window !== "undefined") {
        (window as typeof window & { __affinityChartCy?: Core | null }).__affinityChartCy = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!browserRef.current) {
      return;
    }

    browserRef.current.focus();
  }, [focusRequestToken]);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) {
      return;
    }

    cy.elements().unselect();

    if (selection.kind !== "none") {
      cy.$id(selection.selectedId).select();
    }
  }, [selection]);

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || nodes.length === 0) {
      previousLayoutRevisionRef.current = layoutRevision;
      previousNodeIdsKeyRef.current = nodeIdsKey;
      previousPositionsKeyRef.current = positionsKey;
      return;
    }

    const hasSavedPositions = Object.keys(positions).length > 0;
    const layoutRevisionChanged = previousLayoutRevisionRef.current !== layoutRevision;
    const nodeIdsChanged = previousNodeIdsKeyRef.current !== nodeIdsKey;
    const positionsWereEmpty =
      previousPositionsKeyRef.current === null || previousPositionsKeyRef.current === "{}";

    previousLayoutRevisionRef.current = layoutRevision;
    previousNodeIdsKeyRef.current = nodeIdsKey;
    previousPositionsKeyRef.current = positionsKey;

    if (hasSavedPositions) {
      cy.batch(() => {
        nodes.forEach((node) => {
          const position = positions[node.id];
          if (position) {
            cy.$id(node.id).position(position);
          }
        });
      });

      if (layoutRevisionChanged || nodeIdsChanged || positionsWereEmpty) {
        cy.fit(undefined, 72);
      }

      return;
    }

    cy.layout({
      animate: false,
      fit: true,
      idealEdgeLength: 140,
      name: "cose-bilkent",
      nodeRepulsion: 12000,
      padding: 72,
    } as cytoscape.LayoutOptions).run();
  }, [layoutRevision, nodeIdsKey, nodes, positions, positionsKey]);

  return (
    <Card className="overflow-hidden">
      <CardContent className="space-y-4 px-6 py-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              {t("affinityChart.canvas.eyebrow")}
            </p>
            <p className="text-sm text-muted-foreground">{t("affinityChart.canvas.body")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                cyRef.current?.fit(undefined, 72);
              }}
              type="button"
              variant="outline"
            >
              <Crosshair className="h-4 w-4" />
              {t("affinityChart.actions.fitGraph")}
            </Button>
          </div>
        </div>

        <div
          aria-label={t("affinityChart.canvas.graphAria")}
          className="overflow-hidden rounded-[24px] border border-white/70 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.96),_rgba(242,234,223,0.88))]"
          ref={browserRef}
          tabIndex={-1}
        >
          <CytoscapeComponent
            cy={attachCy}
            elements={elements}
            layout={{ name: "preset" }}
            stylesheet={STYLESHEET}
            style={{ height: "520px", width: "100%" }}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Badge variant="outline">
            {t("affinityChart.canvas.counts", {
              edgeCount: edges.length,
              nodeCount: nodes.length,
            })}
          </Badge>
          <Button onClick={() => setBrowserOpen((open) => !open)} type="button" variant="ghost">
            <Search className="h-4 w-4" />
            {browserOpen
              ? t("affinityChart.actions.hideBrowser")
              : t("affinityChart.actions.browseGraph")}
          </Button>
        </div>

        {browserOpen ? (
          <div className="grid gap-4 rounded-[22px] border border-white/70 bg-secondary/20 p-4 lg:grid-cols-2">
            <div className="space-y-3">
              <p className="text-sm font-semibold text-foreground">
                {t("affinityChart.canvas.visibleNodes")}
              </p>
              <div className="max-h-64 space-y-2 overflow-auto pr-2">
                {nodes.map((node) => (
                  <Button
                    aria-label={`Node: ${node.label}`}
                    className="w-full justify-start"
                    key={node.id}
                    onClick={() => onNodeSelect(node.id)}
                    type="button"
                    variant={
                      selection.kind === "node" && selection.selectedId === node.id
                        ? "default"
                        : "outline"
                    }
                  >
                    <span className="truncate">{node.label}</span>
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-sm font-semibold text-foreground">
                {t("affinityChart.canvas.visibleEdges")}
              </p>
              <div className="max-h-64 space-y-2 overflow-auto pr-2">
                {edges.map((edge) => (
                  <Button
                    aria-label={`Edge: ${edge.sourceLabel} -> ${edge.targetLabel}`}
                    className="w-full justify-start"
                    key={edge.id}
                    onClick={() => onEdgeSelect(edge.id)}
                    type="button"
                    variant={
                      selection.kind === "edge" && selection.selectedId === edge.id
                        ? "default"
                        : "outline"
                    }
                  >
                    <span className="truncate">
                      {edge.sourceLabel} {"->"} {edge.targetLabel}
                    </span>
                    <span className="ml-auto text-xs opacity-80">{edge.label}</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
